import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type SpaceDefinition = Awaited<ReturnType<typeof trpc.spaces.list.query>>[number];

export function useSpaces() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const spacesQuery = useQuery(trpc.spaces.list.queryOptions());
  const createMutation = useMutation(trpc.spaces.create.mutationOptions());
  const updateMutation = useMutation(trpc.spaces.update.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      await spacesQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load spaces.");
    }
  }, [spacesQuery.refetch]);

  const applySpacePatch = useCallback((input: { name: string; spaceId: string }) => {
    queryClient.setQueryData<SpaceDefinition[]>(trpc.spaces.list.queryKey(), (current = []) =>
      current.map((space) => (space.id === input.spaceId ? { ...space, name: input.name } : space)),
    );
  }, [queryClient, trpc]);

  async function createSpace(input: { description: string; name: string }) {
    setError(null);
    try {
      const created = await createMutation.mutateAsync(input);
      queryClient.setQueryData<SpaceDefinition[]>(trpc.spaces.list.queryKey(), (current = []) =>
        [...current, created].sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name)),
      );
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create space.");
      throw err;
    }
  }

  async function updateSpace(input: { name: string; spaceId: string }) {
    setError(null);
    const previousData = spacesQuery.data;
    applySpacePatch(input);
    try {
      queryClient.setQueryData(trpc.spaces.list.queryKey(), await updateMutation.mutateAsync(input));
    } catch (err) {
      queryClient.setQueryData(trpc.spaces.list.queryKey(), previousData);
      setError(err instanceof Error ? err.message : "Could not update space.");
      throw err;
    }
  }

  return {
    applySpacePatch,
    createSpace,
    data: spacesQuery.data ?? [],
    error: error ?? (spacesQuery.error instanceof Error ? spacesQuery.error.message : spacesQuery.error ? String(spacesQuery.error) : null),
    isCreating: createMutation.isPending || updateMutation.isPending,
    isLoading: spacesQuery.isPending,
    reload,
    updateSpace,
  };
}
